import React from 'react';
import { X, ArrowLeft, Briefcase, Cpu, Globe, Shield, Zap, ArrowRight } from 'lucide-react';

export default function Services({ onClose }) {
    const services = [
        {
            icon: <Cpu size={32} />,
            title: "AI Transformation Strategy",
            description: "We help organizations move beyond retrofitting to AI-native operations. Our strategy services define the roadmap for integrating intelligence into every core process.",
            features: ["Organizational Readiness Assessment", "AI Opportunity Mapping", "Implementation Roadmaps", "Change Management"]
        },
        {
            icon: <Globe size={32} />,
            title: "Custom Sapience Systems",
            description: "Designing and building bespoke systems based on our Nonary Lattice framework. We create multi-modal solutions that reason across digital and physical domains.",
            features: ["Data Sourcing and Annotation", "Data to AI Readiness", "Multi-modal Architecture Design", "Custom LLM Fine-tuning", "Autonomous Agent Swarms", "Knowledge Graph Integration"]
        },
        {
            icon: <Zap size={32} />,
            title: "Synthetic Sapience Integration",
            description: "Embedding intelligence into physical and digital environments seamlessly. We bridge the gap between abstract reasoning and material action.",
            features: ["IoT & Edge AI Deployment", "Computer Vision Systems", "Robotic Process Orchestration", "Real-time Sensor Fusion"]
        },
        {
            icon: <Shield size={32} />,
            title: "Ethical AI Governance",
            description: "Ensuring AI systems are safe, aligned, and human-centric. We provide the framework for responsible intelligence at scale.",
            features: ["Alignment Layer Design", "Bias Mitigation & Auditing", "Safety Protocol Implementation", "Regulatory Compliance"]
        }
    ];

    const processSteps = [
        {
            number: "01",
            title: "Discovery",
            description: "We analyze your existing workflows and identify where intelligence can become a substrate for growth."
        },
        {
            number: "02",
            title: "Design",
            description: "Our architects design a multi-modal system tailored to your specific organizational needs."
        },
        {
            number: "03",
            title: "Deployment",
            description: "We integrate the intelligence layer ambiently, ensuring minimal disruption and maximum impact."
        },
        {
            number: "04",
            title: "Evolution",
            description: "The system continuously learns and adapts, evolving alongside your organization."
        }
    ];

    return (
        <div className="fixed inset-0 bg-white z-50 overflow-y-auto">
            {/* Header */}
            <div className="sticky top-0 bg-white/95 backdrop-blur-md border-b border-gray-200 z-10">
                <div className="max-w-6xl mx-auto px-6 py-4 flex justify-between items-center">
                    <button
                        onClick={onClose}
                        className="flex items-center gap-2 text-gray-700 hover:text-amber-600 transition"
                    >
                        <ArrowLeft size={20} />
                        <span className="font-medium">Back</span>
                    </button>
                    <button
                        onClick={onClose}
                        className="text-gray-700 hover:text-amber-600 transition"
                    >
                        <X size={24} />
                    </button>
                </div>
            </div>

            {/* Content */}
            <div className="max-w-6xl mx-auto px-6 py-16">
                {/* Hero */}
                <div className="mb-16">
                    <div className="inline-flex items-center gap-2 px-4 py-2 bg-amber-100 rounded-full border border-amber-300 mb-6">
                        <Briefcase size={16} className="text-amber-600" />
                        <span className="text-amber-900 text-sm font-semibold">Our Services</span>
                    </div>
                    <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6 leading-tight">
                        Infrastructure for the <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-600 to-orange-600">Post-AGI Era</span>
                    </h1>
                    <p className="text-xl text-gray-600 leading-relaxed max-w-4xl">
                        We don't just provide tools; we provide the substrate. Our services are designed to help you navigate the transition from information-based systems to intelligence-based ecosystems.
                    </p>
                </div>

                {/* Services Grid */}
                <section className="mb-24">
                    <div className="grid md:grid-cols-2 gap-8">
                        {services.map((service, idx) => (
                            <div key={idx} className="bg-white rounded-3xl p-8 border border-gray-200 hover:border-amber-300 hover:shadow-xl transition-all duration-300 group">
                                <div className="w-16 h-16 bg-gradient-to-br from-amber-100 to-orange-100 rounded-2xl flex items-center justify-center text-amber-600 mb-6 group-hover:scale-110 transition-transform">
                                    {service.icon}
                                </div>
                                <h3 className="text-2xl font-bold text-gray-900 mb-4">{service.title}</h3>
                                <p className="text-gray-700 leading-relaxed mb-6">
                                    {service.description}
                                </p>
                                <ul className="space-y-3">
                                    {service.features.map((feature, fIdx) => (
                                        <li key={fIdx} className="flex items-center gap-2 text-gray-600">
                                            <div className="w-1.5 h-1.5 bg-amber-500 rounded-full"></div>
                                            {feature}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        ))}
                    </div>
                </section>

                {/* Our Process */}
                <section className="mb-24">
                    <h2 className="text-4xl font-bold text-gray-900 mb-12 text-center">How We Work</h2>
                    <div className="grid md:grid-cols-4 gap-8">
                        {processSteps.map((step, idx) => (
                            <div key={idx} className="relative">
                                <div className="text-6xl font-bold text-gray-100 mb-4 absolute -top-8 -left-4 -z-10">
                                    {step.number}
                                </div>
                                <h3 className="text-xl font-bold text-gray-900 mb-3">{step.title}</h3>
                                <p className="text-gray-600 leading-relaxed">
                                    {step.description}
                                </p>
                            </div>
                        ))}
                    </div>
                </section>

                {/* CTA Section */}
                <section className="bg-gray-900 rounded-3xl p-12 text-center relative overflow-hidden">
                    <div className="absolute top-0 right-0 w-64 h-64 bg-amber-600/10 rounded-full blur-3xl"></div>
                    <div className="absolute bottom-0 left-0 w-64 h-64 bg-orange-600/10 rounded-full blur-3xl"></div>

                    <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">Ready to Build the Future?</h2>
                    <p className="text-xl text-gray-400 mb-10 max-w-2xl mx-auto">
                        Whether you're looking to transform your entire organization or deploy a specific intelligence system, we're here to help.
                    </p>
                    <div className="flex flex-col sm:flex-row gap-4 justify-center">
                        <button className="px-8 py-4 bg-gradient-to-r from-amber-600 to-orange-600 text-white rounded-xl font-bold text-lg hover:shadow-2xl hover:shadow-amber-600/40 transition">
                            Schedule a Consultation
                        </button>
                        <button className="px-8 py-4 bg-white/10 text-white rounded-xl font-bold text-lg hover:bg-white/20 transition border border-white/20">
                            Download Framework
                        </button>
                    </div>
                </section>
            </div>
        </div>
    );
}
